import { Controller } from "@hotwired/stimulus"
import { animate, inView, stagger } from "motion"

// Scroll-reveal animations powered by Motion (motion.dev).
//
//   <section data-controller="reveal">
//     <div data-reveal>…</div>              <- fades/slides in when scrolled to
//     <div data-reveal-group>…children…</div> <- same, staggered per child
//   </section>
//
// The hidden start state is applied from JS, never from CSS, so the content is
// always visible when JS is unavailable. Once an element has animated in, its
// inline styles are cleared so nothing can leave it stuck half-visible (Motion
// commits styles on the animation frame loop, which browsers throttle in
// background tabs — a safety timer covers that case too).
//
// Respects prefers-reduced-motion.
export default class extends Controller {
    static values = {
        y: { type: Number, default: 24 },   // px offset to travel
        duration: { type: Number, default: 0.5 },
    }

    static SAFETY_MS = 3000

    connect() {
        this.stops = []
        this.timers = []

        this.items = [
            ...this.element.querySelectorAll("[data-reveal]"),
            ...this.element.querySelectorAll("[data-reveal-group]"),
        ]

        // Don't hide anything we might not be able to bring back: with reduced
        // motion, or when the page isn't being painted (background tab), the
        // IntersectionObserver may never fire.
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
        if (document.visibilityState !== "visible") return

        this.items.forEach((el) => this.prepare(el))
        this.prepareCounters()

        // Global failsafe: whatever happens, nothing stays invisible.
        this.timers.push(setTimeout(() => {
            this.items.forEach((el) => this.reveal(this.targetsFor(el)))
        }, this.constructor.SAFETY_MS))
    }

    disconnect() {
        this.stops.forEach((stop) => stop?.())
        this.timers.forEach((t) => clearTimeout(t))
        this.stops = []
        this.timers = []
    }

    targetsFor(el) {
        return el.hasAttribute("data-reveal-group") ? [...el.children] : [el]
    }

    prepare(el) {
        const group = el.hasAttribute("data-reveal-group")
        const targets = this.targetsFor(el)

        targets.forEach((t) => {
            t.style.opacity = "0"
            t.style.transform = `translateY(${this.yValue}px) scale(0.97)`
            t.style.filter = "blur(6px)"
        })

        const stop = inView(el, () => {
            const animation = animate(
                targets,
                {
                    opacity: [0, 1],
                    y: [this.yValue, 0],
                    scale: [0.97, 1],
                    filter: ["blur(6px)", "blur(0px)"],
                },
                {
                    duration: this.durationValue,
                    delay: group ? stagger(0.09) : 0,
                    ease: [0.22, 1, 0.36, 1],
                },
            )

            animation.finished.then(() => this.reveal(targets)).catch(() => this.reveal(targets))

            // Safety net: if the frame loop is throttled (background tab) the
            // animation may never commit — make sure the content shows anyway.
            const ms = (this.durationValue + targets.length * 0.08) * 1000 + 1000
            this.timers.push(setTimeout(() => this.reveal(targets), ms))

            return () => {} // reveal once; don't reverse on exit
        }, { amount: 0.15 })

        this.stops.push(stop)
    }

    // Hand the element back to CSS — no inline opacity/transform left behind.
    reveal(targets) {
        targets.forEach((t) => {
            t.style.opacity = ""
            t.style.transform = ""
            t.style.filter = ""
        })
    }

    // Count stat numbers up when they scroll into view. The final value is
    // already in the markup, so without JS (or with reduced motion) it just
    // shows the number.
    prepareCounters() {
        this.element.querySelectorAll("[data-count]").forEach((el) => {
            const target = Number(el.textContent.trim())
            if (!Number.isFinite(target)) return

            const stop = inView(el, () => {
                animate(0, target, {
                    duration: 1.1,
                    ease: [0.22, 1, 0.36, 1],
                    onUpdate: (value) => { el.textContent = Math.round(value) },
                })
                // Failsafe: make sure the real number is shown regardless.
                this.timers.push(setTimeout(() => { el.textContent = target }, 2500))
                return () => {}
            }, { amount: 0.5 })

            this.stops.push(stop)
        })
    }
}
